// TODO [m|8]: this component overcomlicated, need to refactor usind useToothTransform.ts hook
// with simple UI squares with tooth number, color and active state determined by tooth id from url
// Just make simple grid like in TeethChart.tsx
import cn from 'classnames';
import { FC } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { LocationStateType, PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { scrollToElementByID, trackEvent } from '@/shared/lib';

import { reportsModel } from '@/entities/reports';
import {
  JawQuartile,
  makeJawObject,
  Quartiles,
  toothModel,
} from '@/entities/tooth';

import { getCombinedTeeth } from '../../lib';

import styles from './SimplifiedToothChart.module.scss';

type SimplifiedToothChartProps = {
  reportID: string;
  patientID: string;
  className?: string;
  toothOfInterestNumber?: number;
  useScroll?: boolean;
  handleToothClick?: () => void;
  mergePrimaryWithAdult?: boolean;
  isToothPage?: boolean;
};

export const SimplifiedToothChart: FC<SimplifiedToothChartProps> = (props) => {
  const {
    reportID,
    className,
    toothOfInterestNumber,
    useScroll,
    handleToothClick,
    mergePrimaryWithAdult = false,
    isToothPage = false,
  } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();

  // type casting is necessary for typification location.state
  const locationState = location?.state as LocationStateType;

  const teeth = useAppSelector(toothModel.selectors.selectByReportID(reportID));
  const activeToothID = useAppSelector(
    reportsModel.selectors.selectActiveToothID,
  );
  const localROITeethIDs = useAppSelector(
    toothModel.selectors.selectLocalROITeethIDs,
  );

  const activeTeethISOs = mergePrimaryWithAdult
    ? localROITeethIDs
        .map((id) => {
          const tooth = teeth.find((tooth) => tooth.ID === id);
          return tooth?.Numeration?.ISO ?? 0;
        })
        .filter((iso) => iso !== 0)
    : undefined;

  const showPrimaryTeeth =
    teeth.some((tooth) => (tooth.Numeration?.ISO ?? 0) > 50) &&
    !mergePrimaryWithAdult;

  const jawObject = makeJawObject(teeth);

  const mainTeeth = {
    quartile1: jawObject.quartile1,
    quartile2: jawObject.quartile2,
    quartile3: jawObject.quartile3,
    quartile4: jawObject.quartile4,
  };

  const childTeeth = {
    primaryQuartile1: jawObject?.primaryQuartile1 ?? [],
    primaryQuartile2: jawObject?.primaryQuartile2 ?? [],
    primaryQuartile3: jawObject?.primaryQuartile3 ?? [],
    primaryQuartile4: jawObject?.primaryQuartile4 ?? [],
  };

  const onToothClick = (toothID: string, ISONumber: number) => {
    trackEvent({
      eventName: isToothPage
        ? 'ToothPageToothFormulaButtonClicked'
        : 'ReportPageToothFormulaButtonClicked',
      properties: {
        tooth_number: ISONumber,
      },
    });

    if (handleToothClick) {
      handleToothClick();
    }

    dispatch(reportsModel.actions.setActiveToothID(toothID));

    if (useScroll) {
      scrollToElementByID(toothID);
    } else {
      navigate(
        {
          pathname: generatePath('../../' + PATHS.gpReportAllSlices, {
            toothID,
          }),
        },
        {
          state: {
            ...locationState,
            toothID,
          },
          relative: 'path',
        },
      );
    }
  };

  const displayTeeth = mergePrimaryWithAdult
    ? getCombinedTeeth(mainTeeth, childTeeth)
    : mainTeeth;

  return (
    <>
      <div
        className={cn(styles.container, className)}
        data-component="simplified-tooth-chart"
      >
        {Object.entries(displayTeeth).map(([quartile, quartileTeeth]) => (
          <JawQuartile
            key={quartile}
            className={cn(styles[quartile])}
            reportID={reportID}
            quartile={quartile as Quartiles}
            teeth={quartileTeeth}
            onToothClick={onToothClick}
            toothOfInterestNumber={toothOfInterestNumber}
            isSimplify
            focusedToothID={activeToothID}
            ROIList={activeTeethISOs}
          />
        ))}
      </div>

      {showPrimaryTeeth && (
        <div className={cn(styles.container, styles.primary, className)}>
          {Object.entries(childTeeth).map(([quartile, quartileTeeth]) => (
            <JawQuartile
              key={quartile}
              className={cn(styles[quartile])}
              reportID={reportID}
              quartile={quartile as Quartiles}
              teeth={quartileTeeth}
              onToothClick={onToothClick}
              toothOfInterestNumber={toothOfInterestNumber}
              isSimplify
              focusedToothID={activeToothID}
              ROIList={activeTeethISOs}
            />
          ))}
        </div>
      )}
    </>
  );
};
